import React, {useState, useEffect, useCallback} from 'react';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { Box, Grid, Typography } from '@mui/material';
import { PaginationBar } from 'Shared/PaginationBar';
import { UserIntegrationItem } from 'Shared/integrations/UserIntegrationItem';
import { UserIntegrationItemPreDefined } from 'Shared/integrations/UserIntegrationItemPreDefined';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Logo_PNG from 'Assets/img/Infintegration_Logo_Primary.png';

const IntegrationsListPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [userIntegrationsList, setUserIntegrationsList] = useState([]);
    const [integrationsList, setIntegrationsList] = useState([]);

    // Pagination state for "My integrations"
    const [myIntPage, setMyIntPage] = useState(0);
    const [myIntRowsPerPage, setMyIntRowsPerPage] = useState(10);
    const [myIntTotal, setMyIntTotal] = useState(0);

    // Pagination state for "Library"
    const [libIntPage, setLibIntPage] = useState(0);
    const [libIntRowsPerPage, setLibIntRowsPerPage] = useState(10);
    const [libIntTotal, setLibIntTotal] = useState(0);

    const getListsIntegrations = useCallback(() => {
        dispatch(setLoading(true));
        const libPagination = JSON.stringify({ pageSize: libIntRowsPerPage, currentPage: libIntPage });
        const myPagination = JSON.stringify({ pageSize: myIntRowsPerPage, currentPage: myIntPage });
        axios.all([
            cachedGet(`/integrations?pagination=${encodeURIComponent(libPagination)}`),
            cachedGet(`/user/integration/list?pagination=${encodeURIComponent(myPagination)}`)
        ]).then(
            axios.spread((firstResponse, secondResponse) => {
                setIntegrationsList(firstResponse.data.integrations);
                setLibIntTotal(firstResponse.data.total ?? firstResponse.data.integrations.length);
                setUserIntegrationsList(secondResponse.data.integrations);
                setMyIntTotal(secondResponse.data.total ?? secondResponse.data.integrations.length);
                dispatch(setLoading(false));
            })
        ).catch(() => {
            dispatch(setLoading(false));
        });
    }, [dispatch, libIntPage, libIntRowsPerPage, myIntPage, myIntRowsPerPage]);

    const getMyIntegrationList = useCallback(() => {
        const pagination = JSON.stringify({ pageSize: myIntRowsPerPage, currentPage: myIntPage });
        cachedGet(`/user/integration/list?pagination=${encodeURIComponent(pagination)}`).then((res) => {
            setUserIntegrationsList(res.data.integrations);
            setMyIntTotal(res.data.total ?? res.data.integrations.length);
        }).catch(() => {
        });
    }, [myIntPage, myIntRowsPerPage]);

    const linkIntegrationToUser = (item) => {
        dispatch(setLoading(true));
        http.post(`/user/integration/${item.id}/link`).then(() => {
            getMyIntegrationList();
            dispatch(setLoading(false));
            enqueueSnackbar('Integration was added to your list', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    const unlinkIntegration = (item) => {
        dispatch(setLoading(true));
        http.delete(`/user/integration/${item.id}/delete`).then((res) => {
            getMyIntegrationList();
            dispatch(setLoading(false));
            enqueueSnackbar('Integration was unlinked', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    useEffect(() => {
        getListsIntegrations();
    }, [getListsIntegrations]);

    return (
        <>
            <Grid container sx={{height: '100%'}}>
                <Grid item xs={3} sx={{paddingRight: '20px'}}>
                    <Box
                        sx={{
                            height: 'calc(100vh - 30px)',
                            backgroundColor: '#f9f7f5',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '3rem',
                            position: 'sticky',
                            top: '15px'
                        }}
                    >
                        <img style={{width: '100%'}} src={Logo_PNG} alt=""/>
                    </Box>
                </Grid>

                <Grid item xs={9}>
                    <Box
                        sx={{
                            padding: '30px 25px 25px 25px',
                            height: '100%',
                            backgroundColor: '#FAFEFF',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            variant="h2"
                            color="primary.blackText"
                            sx={{
                                marginBottom: '20px',
                                textAlign: 'center',
                                fontWeight: 500,
                                fontSize: '20px',
                                lineHeight: '24px',
                            }}
                        >
                            My integrations list:
                        </Typography>
                        {!userIntegrationsList.length && (
                            <Typography
                                sx={{
                                    marginBottom: '20px',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    color: '#D76A6A',
                                }}
                            >
                                Your integrations list is empty
                            </Typography>
                        )}

                        {userIntegrationsList.map(({ name, ...rest }) => (
                            <UserIntegrationItem
                                key={name}
                                item={{ name, ...rest }}
                                unlinkIntegration={() => unlinkIntegration({ name, ...rest })}
                            />
                        ))}

                        {myIntTotal > 0 && (
                            <PaginationBar
                                count={myIntTotal}
                                page={myIntPage}
                                onPageChange={(e, newPage) => setMyIntPage(newPage)}
                                rowsPerPage={myIntRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setMyIntRowsPerPage(parseInt(e.target.value, 10));
                                    setMyIntPage(0);
                                }}
                            />
                        )}

                        <Typography
                            variant="h2"
                            color="primary.blackText"
                            sx={{
                                marginTop: '46px',
                                marginBottom: '20px',
                                textAlign: 'center',
                                fontWeight: 500,
                                fontSize: '20px',
                                lineHeight: '24px',
                            }}
                        >
                            Infintegration Library of Integrations:
                        </Typography>

                        {integrationsList.map(({ name, ...rest }) => (
                            <UserIntegrationItemPreDefined
                                key={name}
                                item={{ name, ...rest }}
                                linkIntegrationToUser={() => linkIntegrationToUser({ name, ...rest })}
                            />
                        ))}

                        {libIntTotal > 0 && (
                            <PaginationBar
                                count={libIntTotal}
                                page={libIntPage}
                                onPageChange={(e, newPage) => setLibIntPage(newPage)}
                                rowsPerPage={libIntRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setLibIntRowsPerPage(parseInt(e.target.value, 10));
                                    setLibIntPage(0);
                                }}
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default IntegrationsListPage;
